// Der Zurück-Knopf schließt, was offen ist.
//
// ══ Das Problem ══
//
// Auf Android drückt man den Zurück-Knopf (oder wischt von der Kante), um ein
// offenes Blatt zu schließen. In einer nativen App ist das selbstverständlich.
// In einer PWA passiert stattdessen: Der Browser navigiert eine Seite zurück —
// das Blatt bleibt offen, aber dahinter ist eine andere Seite. Oder man fliegt
// aus der App heraus. Nichts schreit lauter "das ist eine Webseite".
//
// ══ Die Lösung ══
//
// Ein offenes Overlay legt einen Eintrag in die History. Der Zurück-Knopf holt
// ihn wieder ab — und statt zu navigieren, schließt sich das Overlay. Wird per
// Knopf geschlossen, räumen wir den Eintrag selbst wieder ab.
//
// ══ Warum das hier so umständlich aussieht ══
//
// Weil die naive Fassung nicht funktioniert, und zwar spektakulär.
//
// React ruft Effekte im Entwicklungsmodus absichtlich DOPPELT auf:
// aufsetzen → abräumen → aufsetzen. Das findet Fehler wie diesen. Räumt man im
// Abräumen mit `history.back()` auf, dann:
//
//   1. Effekt setzt auf, legt einen History-Eintrag an.
//   2. Abräumen ruft `history.back()` — das wirkt aber erst einen Tick später.
//   3. Effekt setzt erneut auf und legt NOCH einen Eintrag an.
//   4. Jetzt erst kommt das `back()` von Schritt 2 an.
//
// Die Bilanz stimmt nicht mehr. Beobachtet: Ein Blatt auf der Team-Seite öffnen,
// per ✕ schließen — und die App stand auf /crm statt auf /crm/admin/team. Der
// Nutzer wird aus dem Modul geworfen, ohne etwas falsch gemacht zu haben.
// (Davor, in einer noch früheren Fassung, schloss sich das Blatt sofort nach dem
// Öffnen wieder von selbst.)
//
// Deshalb drei Vorkehrungen:
//
//   • EIN popstate-Listener für die ganze App und ein Stapel offener Overlays —
//     statt eines Listeners pro Overlay, der fremde Ereignisse mitbekommt.
//
//   • Das Abräumen läuft eine Zeitscheibe verzögert. Ein Doppelaufruf von React
//     bricht es ab (der Effekt setzt sofort wieder auf), ein echtes Schließen
//     lässt es durch. Das ist der einzige verlässliche Unterschied zwischen
//     beiden — im Abräumen selbst sind sie nicht auseinanderzuhalten.
//
//   • Das Abräumen räumt NUR ab, wenn der eigene Marker noch oben liegt. Genau
//     hier saß ein zweiter Bug: Die Seitenleiste schließt sich beim Klick auf ein
//     Modul UND navigiert gleichzeitig weiter. Die Navigation legt die Zielseite
//     oben auf den Stack — und das blinde `history.back()` warf sie sofort wieder
//     runter. Man tippte "Kundenstamm" und landete zurück auf der Startseite.
//     Liegt der eigene Marker nicht mehr oben, hat die Navigation ihn längst
//     ersetzt: dann gibt es nichts abzuräumen.

import { useEffect, useRef } from "react";

/** Offene Overlays, das zuletzt geöffnete liegt oben. */
const stapel: Array<() => void> = [];

/** Wie viele popstate-Ereignisse stammen aus unserem eigenen Aufräumen? */
let selbstAusgeloest = 0;

/** Vergibt jedem Overlay-Marker eine eigene Nummer, damit man ihn im History-State wiedererkennt. */
let markenZaehler = 0;

let installiert = false;

function installiere() {
  if (installiert) return;
  installiert = true;

  window.addEventListener("popstate", () => {
    // Unser eigenes history.back() aus dem Aufräumen — kein echter Zurück-Druck.
    if (selbstAusgeloest > 0) {
      selbstAusgeloest--;
      return;
    }
    stapel.pop()?.();
  });
}

/**
 * @param offen       Ist das Overlay gerade sichtbar?
 * @param schliessen  Wird gerufen, wenn der Zurück-Knopf gedrückt wird.
 */
export function useZurueckTaste(offen: boolean, schliessen: () => void) {
  // Fast jeder Aufrufer schreibt `onClose={() => setOffen(false)}` — eine neue
  // Funktion bei JEDEM Render. Stünde sie in den Dependencies, liefe der Effekt
  // ständig neu und legte ständig History-Einträge an.
  const merker = useRef(schliessen);
  merker.current = schliessen;

  // Überlebt den Doppelaufruf von React: Es ist dieselbe Komponenteninstanz, also
  // bleibt die Ref erhalten. Genau daran erkennen wir, dass unser Eintrag noch
  // liegt und kein zweiter dazugehört.
  const eintragLiegt = useRef(false);
  const markeRef = useRef(0);
  const abraeumer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!offen) return;

    // Ein noch schwebendes Abräumen abbrechen: Wir sind offensichtlich wieder da,
    // also war das eben nur Reacts Doppelaufruf und kein echtes Schließen.
    clearTimeout(abraeumer.current);

    installiere();

    const eintrag = () => merker.current();
    stapel.push(eintrag);

    if (!eintragLiegt.current) {
      eintragLiegt.current = true;
      markeRef.current = ++markenZaehler;
      window.history.pushState({ ...window.history.state, zurueckMarke: markeRef.current }, "");
    }

    return () => {
      const i = stapel.indexOf(eintrag);

      // Schon vom Stapel geholt? Dann war es der Zurück-Knopf — der hat den
      // History-Eintrag bereits mitgenommen, es gibt nichts abzuräumen.
      if (i < 0) {
        eintragLiegt.current = false;
        return;
      }
      stapel.splice(i, 1);

      // Verzögert, damit Reacts Doppelaufruf es noch abbrechen kann. Läuft es
      // durch, wurde wirklich per Knopf geschlossen — dann muss unser Eintrag
      // weg, sonst bräuchte man hinterher einen zusätzlichen Druck auf Zurück,
      // um die Seite zu verlassen.
      abraeumer.current = setTimeout(() => {
        if (!eintragLiegt.current) return;
        eintragLiegt.current = false;

        // NUR abräumen, wenn der eigene Marker noch ganz oben liegt. Hat sich
        // beim Schließen zugleich die Route geändert (Seitenleiste → Modul), liegt
        // dort schon die Zielseite — ein `back()` würde sie wegnehmen. Dann ist
        // der Marker längst ersetzt und es gibt nichts mehr zu tun.
        if (window.history.state?.zurueckMarke !== markeRef.current) return;

        selbstAusgeloest++;
        window.history.back();
      }, 0);
    };
  }, [offen]);
}
